import type { ReactNode, CSSProperties, ElementType } from 'react';

type Spacing = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface BoxProps {
  children: ReactNode;
  /**
   * Padding on all sides
   */
  padding?: Spacing;
  /**
   * Horizontal padding (left and right)
   */
  paddingX?: Spacing;
  /**
   * Vertical padding (top and bottom)
   */
  paddingY?: Spacing;
  /**
   * Margin on all sides
   */
  margin?: Spacing;
  /**
   * Horizontal margin
   */
  marginX?: Spacing;
  /**
   * Vertical margin
   */
  marginY?: Spacing;
  /**
   * HTML element to render
   * @default 'div'
   */
  as?: ElementType;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const spacingMap: Record<Spacing, string> = {
  none: '0',
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '3rem',
};

/**
 * Box component for consistent spacing and layout container.
 *
 * @example
 * <Box padding="lg" marginY="md">
 *   <Card />
 * </Box>
 */
export function Box({
  children,
  padding,
  paddingX,
  paddingY,
  margin,
  marginX,
  marginY,
  as: Component = 'div',
  className = '',
  'data-testid': testId,
}: BoxProps) {
  const style: CSSProperties = {};

  // Handle padding
  if (padding) {
    style.padding = spacingMap[padding];
  }
  if (paddingX) {
    style.paddingLeft = spacingMap[paddingX];
    style.paddingRight = spacingMap[paddingX];
  }
  if (paddingY) {
    style.paddingTop = spacingMap[paddingY];
    style.paddingBottom = spacingMap[paddingY];
  }

  // Handle margin
  if (margin) {
    style.margin = spacingMap[margin];
  }
  if (marginX) {
    style.marginLeft = spacingMap[marginX];
    style.marginRight = spacingMap[marginX];
  }
  if (marginY) {
    style.marginTop = spacingMap[marginY];
    style.marginBottom = spacingMap[marginY];
  }

  return (
    <Component className={className} style={style} data-testid={testId}>
      {children}
    </Component>
  );
}
